// Lock-on lines (docs/ART-DIRECTION.md §2.4, §2.5, GDD §14): one line from every weapon to its locked target, in the
// weapon owner's faction colour, the brightest element of the war after HP. Rebuilt per frame by LockLineSystem.

import { Vector3, type Color } from 'three'
import type { OwnerValue, PlayerId } from '@taxwar/net'
import type { WarCapacity } from '../../match/warSetup'
import { GLOBE_ALTITUDE, latLonToVector3 } from '../geo/globeSpace'
import { factionRole, type Palette } from '../palette'
import { LineBatch } from './LineBatch'

const LOCK_LINE_INTENSITY = 1.6
/** Lines run just above the network so they never sit under geography (ART-DIRECTION §2.4). */
const LOCK_LINE_ALTITUDE = GLOBE_ALTITUDE.network + 0.004

export class LockLineLayer {
  private readonly batch: LineBatch
  private readonly from = new Vector3()
  private readonly to = new Vector3()
  private palette: Palette

  constructor(
    private readonly viewer: PlayerId,
    capacity: WarCapacity,
    palette: Palette,
  ) {
    this.palette = palette
    // Every weapon can hold one lock: city guns, one per unit entity, SAM Sites (buildings).
    this.batch = new LineBatch(capacity.cityGuns + capacity.units + capacity.buildings, LOCK_LINE_INTENSITY)
  }

  get root(): LineBatch['lines'] {
    return this.batch.lines
  }

  begin(): void {
    this.batch.begin()
  }

  add(owner: OwnerValue, fromLatDeg: number, fromLonDeg: number, toLatDeg: number, toLonDeg: number): void {
    latLonToVector3(fromLatDeg, fromLonDeg, LOCK_LINE_ALTITUDE, this.from)
    latLonToVector3(toLatDeg, toLonDeg, LOCK_LINE_ALTITUDE, this.to)
    this.batch.push(this.from, this.to, this.colourOf(owner))
  }

  end(): void {
    this.batch.end()
  }

  /** Lines are rebuilt every frame, so the next frame shows the new palette. */
  setPalette(palette: Palette): void {
    this.palette = palette
  }

  dispose(): void {
    this.batch.dispose()
  }

  private colourOf(owner: OwnerValue): Color {
    return this.palette[factionRole(owner, this.viewer)]
  }
}
